import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaWhatsapp, FaInstagram, FaEnvelope } from "react-icons/fa";
import logo from "../Components/Assets/fabhub-logo-nav.png";
import { backend_url } from "../App";

// Target launch date — update this to the real launch moment.
const LAUNCH_DATE = new Date("2026-10-02T18:00:00");
// When the countdown "started" — only used to draw the progress bar below
// the countdown, so it doesn't need to be exact.
const COUNTDOWN_START = new Date("2026-09-01T00:00:00");

const pad = (n) => String(n).padStart(2, "0");

const HIGHLIGHTS = [
  { icon: "🧵", text: "Premium Fabrics" },
  { icon: "✨", text: "Custom Tailoring" },
  { icon: "🚚", text: "Pan-India Delivery" },
  { icon: "🔒", text: "Secure Checkout" },
];

const ComingSoon = () => {
  const [now, setNow] = useState(Date.now());
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, LAUNCH_DATE.getTime() - now);
  const days = Math.floor(diff / 864e5);
  const hours = Math.floor((diff % 864e5) / 36e5);
  const minutes = Math.floor((diff % 36e5) / 6e4);
  const seconds = Math.floor((diff % 6e4) / 1000);

  const totalSpan = LAUNCH_DATE.getTime() - COUNTDOWN_START.getTime();
  const elapsed = now - COUNTDOWN_START.getTime();
  const progressPct = Math.min(100, Math.max(0, (elapsed / totalSpan) * 100));

  const handleNotify = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${backend_url}/api/notify-launch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.message || "Something went wrong. Please try again.");
        return;
      }
      toast.success("You're on the list! We'll notify you at launch. 🎉");
      setEmail("");
    } catch {
      toast.error("Couldn't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const timeUnits = [
    { label: "Days", value: days },
    { label: "Hours", value: hours },
    { label: "Minutes", value: minutes },
    { label: "Seconds", value: seconds },
  ];

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-10"
      style={{ background: "linear-gradient(135deg,var(--brand-primary-950) 0%,var(--brand-primary-900) 45%,var(--brand-primary-600) 100%)" }}
    >
      {/* Decorative glows */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-16 w-[28rem] h-[28rem] rounded-full bg-fuchsia-400/20 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-[var(--brand-primary-300)]/10 blur-2xl pointer-events-none" />

      {/* Subtle dot grid, matches the texture used on other brand sections */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative w-full max-w-2xl text-center text-white">
        {/* Logo */}
        <div className="flex mx-auto items-center justify-center w-20 h-20 rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur mb-8">
          <img crossOrigin="anonymous" src={logo} alt="Aavryaa FabHub" className="h-12" />
        </div>

        {/* Badge */}
        <div className="flex w-fit mx-auto items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 ring-1 ring-white/20 text-xs font-semibold tracking-widest uppercase mb-6">
          <span className="w-2 h-2 rounded-full bg-fuchsia-300 animate-pulse" />
          Weaving something special
        </div>

        {/* Heading */}
        <h1 className="text-4xl sm:text-6xl font-black leading-tight mb-4">
          Launching{" "}
          <span
            className="text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(90deg,#F5D0FE,var(--brand-primary-300),#FBCFE8)" }}
          >
            Soon
          </span>
        </h1>

        <p className="text-[var(--brand-primary-100)]/90 text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
          Our new collection of premium, unique fabrics and sarees is almost here.
          Crafted to preserve tradition with a modern, elegant touch — stay tuned for the reveal.
        </p>

        {/* Countdown */}
        <div className="flex justify-center items-center gap-2 sm:gap-3 mb-4">
          {timeUnits.map((u, i) => (
            <React.Fragment key={u.label}>
              <div className="w-[70px] sm:w-24 rounded-2xl bg-white/10 ring-1 ring-white/15 backdrop-blur px-2 py-3 sm:py-4 shadow-[0_8px_30px_rgba(0,0,0,0.15)] hover:bg-white/[0.14] transition-colors">
                <div
                  className="text-2xl sm:text-4xl font-black tabular-nums leading-none text-transparent bg-clip-text"
                  style={{ backgroundImage: "linear-gradient(180deg,#fff,var(--brand-primary-100))" }}
                >
                  {pad(u.value)}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-[var(--brand-primary-200)] mt-1.5">{u.label}</div>
              </div>
              {i < timeUnits.length - 1 && (
                <span className="text-xl sm:text-2xl font-bold text-white/25 -mt-4 sm:-mt-5">:</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Launch progress */}
        <div className="max-w-xs mx-auto mb-10">
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000 ease-linear"
              style={{
                width: `${progressPct}%`,
                background: "linear-gradient(90deg,var(--brand-primary-300),#FBCFE8)",
              }}
            />
          </div>
          <p className="text-[11px] text-[var(--brand-primary-200)]/80 mt-2">
            {progressPct.toFixed(0)}% of the way to launch
          </p>
        </div>

        {/* Brand highlights */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {HIGHLIGHTS.map(({ icon, text }) => (
            <span
              key={text}
              className="flex items-center gap-1.5 bg-white/10 ring-1 ring-white/15 text-white/90 text-xs font-medium px-3.5 py-1.5 rounded-full backdrop-blur"
            >
              <span>{icon}</span>
              {text}
            </span>
          ))}
        </div>

        {/* Notify form */}
        <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-2">
          <div className="relative flex-1">
            <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={submitting}
              className="w-full rounded-xl pl-10 pr-4 py-3 text-sm text-gray-800 bg-white/95 placeholder-gray-400 outline-none focus:ring-4 focus:ring-white/30 transition disabled:opacity-60"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl px-6 py-3 text-sm font-bold text-[var(--brand-primary-800)] bg-white hover:bg-[var(--brand-primary-50)] active:scale-[0.98] transition shadow-lg whitespace-nowrap disabled:opacity-60 disabled:active:scale-100"
          >
            {submitting ? "Sending…" : "Notify Me"}
          </button>
        </form>
        <p className="text-[11px] text-[var(--brand-primary-200)]/60 mb-8">
          Be first in line for our launch-day offers — you'll hear from us the moment we open the doors.
        </p>

        {/* Social */}
        <div className="flex items-center justify-center gap-3">
          <a
            href="https://wa.me/919789389489?text=Hi%2C%20I%27d%20love%20to%20know%20about%20your%20upcoming%20launch"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="w-10 h-10 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center hover:bg-white/20 transition"
          >
            <FaWhatsapp />
          </a>
          <a
            href="https://www.instagram.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="w-10 h-10 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center hover:bg-white/20 transition"
          >
            <FaInstagram />
          </a>
        </div>

        <p className="text-[11px] text-[var(--brand-primary-300)]/70 mt-10">
          © {new Date().getFullYear()} Aavryaa FabHub — Preserving unique fabrics, elegantly.
        </p>
      </div>
    </div>
  );
};

export default ComingSoon;
